/**
 * showdownMoves.batch7 — batch 7 of the per-move Showdown animation ports.
 *
 * Each entry is a near-verbatim port of the move's script from Pokémon Showdown's
 * `BattleMoveAnims` (`battle-animations-moves.ts`, MIT/CC0), with the shared
 * templates it delegates to (`BattleOtherAnims.*` from `battle-animations.ts`)
 * either imported from our ported {@link module:showdownOther} or, where a template
 * was not exported there, inlined here as a local helper. Our {@link FxScene} +
 * {@link Actor} mirror Showdown's `scene` + `Sprite`, so the literal
 * `scene.showEffect(...)` / `actor.anim(...)` / `scene.wait(...)` sequences port
 * unchanged.
 *
 * Effect-name discipline: only effect keys vendored in our fx catalog
 * (client/data/generated/fx/catalog.json) are used. Where Showdown referenced an
 * effect/background we did not vendor — a remote-url background image
 * (`url('…/weather-*.jpg')`, `bg-space.jpg`, `bg-icecave.jpg`), the `$bg` shake, or
 * the attacker's own sprite as an effect (`attacker.sp`) — the call is adapted to
 * the nearest vendored effect, substituted with a `backgroundEffect` colour wash,
 * or omitted. Each such site is noted inline. The motion (the `actor.anim`
 * choreography) is always preserved verbatim.
 *
 * Charging moves (skydrop, skullbash, skyattack, solarblade) use the
 * `{ anim, prepareAnim }` object form, matching the existing bespoke entries.
 *
 * Test/runtime separation: this module imports ONLY TYPES from `showdownFx.ts`
 * (that file pulls in Phaser, which can't load in the node test env). Every script
 * here is a pure function over the `FxScene`/`Actor` interfaces.
 */
import type { FxScene, Actor, MoveAnim, MoveAnimEntry } from './showdownFx.js';
import { contactattack, slashattack, fastattack } from './showdownOther.js';

/* ──────────────────────────────────────────────────────────────────────────
 * Local template ports (PS `BattleOtherAnims.*` not exported from showdownOther).
 * ──────────────────────────────────────────────────────────────────────── */

/** PS `BattleOtherAnims.dance` — a tiny side-to-side shimmy of the user. */
const dance: MoveAnim = (_scene: FxScene, [attacker]: Actor[]) => {
  attacker.anim({ x: attacker.x - 10 });
  attacker.anim({ x: attacker.x + 10 });
  attacker.anim({ x: attacker.x });
};

/** PS `BattleOtherAnims.selfstatus` — two wisps collapse inward on the user. */
const selfstatus: MoveAnim = (scene: FxScene, [attacker]: Actor[]) => {
  scene.showEffect(
    'wisp',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 2, opacity: 0.2, time: 0 },
    { scale: 0, opacity: 1, time: 300 },
    'linear',
  );
  scene.showEffect(
    'wisp',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 2, opacity: 0.2, time: 200 },
    { scale: 0, opacity: 1, time: 500 },
    'linear',
  );
};

/** PS `BattleOtherAnims.sound` — three wisp rings expand off the user (sound moves). */
const sound: MoveAnim = (scene: FxScene, [attacker]: Actor[]) => {
  scene.showEffect(
    'wisp',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 0, opacity: 0.7, time: 0 },
    { z: attacker.behind(-50), scale: 5, opacity: 0, time: 400 },
    'linear',
  );
  scene.showEffect(
    'wisp',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 0, opacity: 0.7, time: 150 },
    { z: attacker.behind(-50), scale: 5, opacity: 0, time: 600 },
    'linear',
  );
  scene.showEffect(
    'wisp',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 0, opacity: 0.7, time: 300 },
    { z: attacker.behind(-50), scale: 5, opacity: 0, time: 800 },
    'linear',
  );
};

/** PS `BattleOtherAnims.hydroshot` — three water wisps spray onto the defender. */
const hydroshot: MoveAnim = (scene: FxScene, [attacker, defender]: Actor[]) => {
  scene.showEffect(
    'waterwisp',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 0.4, opacity: 0.3 },
    { x: defender.x + 10, y: defender.y + 5, z: defender.behind(30), scale: 1, opacity: 0.6 },
    'decel',
    'explode',
  );
  scene.showEffect(
    'waterwisp',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 0.4, opacity: 0.3, time: 75 },
    { x: defender.x - 10, y: defender.y - 5, z: defender.behind(30), scale: 1, opacity: 0.6 },
    'decel',
    'explode',
  );
  scene.showEffect(
    'waterwisp',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 0.4, opacity: 0.3, time: 150 },
    { x: defender.x, y: defender.y + 5, z: defender.behind(30), scale: 1, opacity: 0.6 },
    'decel',
    'explode',
  );
};

/** PS `BattleOtherAnims.clawattack` — a double swipe with claw marks on the target. */
const clawattack: MoveAnim = (scene: FxScene, [attacker, defender]: Actor[]) => {
  attacker.anim(
    { x: defender.leftof(-30), y: defender.y + 80, z: defender.behind(-30), time: 400 },
    'ballistic',
  );
  attacker.anim({ x: defender.leftof(30), y: defender.y + 5, z: defender.z, time: 100 });
  attacker.anim(
    { x: defender.leftof(30), y: defender.y + 80, z: defender.behind(-30), time: 200 },
    'ballisticUp',
  );
  attacker.anim({ x: defender.leftof(-30), y: defender.y + 5, z: defender.z, time: 100 });
  attacker.anim({ time: 500 }, 'ballistic2Back');
  defender.delay(450);
  defender.anim({ z: defender.behind(20), time: 100 }, 'swing');
  defender.anim({ time: 200 }, 'swing');
  defender.anim({ z: defender.behind(20), time: 100 }, 'swing');
  defender.anim({ time: 300 }, 'swing');

  scene.showEffect(
    'leftclaw',
    { x: defender.x - 20, y: defender.y + 20, z: defender.z, scale: 0, opacity: 1, time: 400 },
    { x: defender.x - 20, y: defender.y + 20, z: defender.z, scale: 3, opacity: 0, time: 700 },
    'linear',
    'fade',
  );
  scene.showEffect(
    'leftclaw',
    { x: defender.x - 20, y: defender.y - 20, z: defender.z, scale: 0, opacity: 1, time: 400 },
    { x: defender.x - 20, y: defender.y - 20, z: defender.z, scale: 3, opacity: 0, time: 700 },
    'linear',
    'fade',
  );
  scene.showEffect(
    'rightclaw',
    { x: defender.x + 20, y: defender.y + 20, z: defender.z, scale: 0, opacity: 1, time: 700 },
    { x: defender.x + 20, y: defender.y + 20, z: defender.z, scale: 3, opacity: 0, time: 1000 },
    'linear',
    'fade',
  );
  scene.showEffect(
    'rightclaw',
    { x: defender.x + 20, y: defender.y - 20, z: defender.z, scale: 0, opacity: 1, time: 700 },
    { x: defender.x + 20, y: defender.y - 20, z: defender.z, scale: 3, opacity: 0, time: 1000 },
    'linear',
    'fade',
  );
};

/** PS `BattleOtherAnims.sneakattack` — the user blinks through the target and back. */
const sneakattack: MoveAnim = (_scene: FxScene, [attacker, defender]: Actor[]) => {
  attacker.anim(
    { x: attacker.leftof(-20), y: attacker.y, z: attacker.behind(-20), opacity: 0, time: 200 },
    'linear',
  );
  attacker.anim(
    { x: defender.x, y: defender.y, z: defender.behind(-120), opacity: 0, time: 1 },
    'linear',
  );
  attacker.anim(
    { x: defender.x, y: defender.y, z: defender.behind(40), opacity: 1, time: 250 },
    'linear',
  );
  attacker.anim(
    { x: defender.x, y: defender.y, z: defender.behind(-5), opacity: 0, time: 300 },
    'linear',
  );
  attacker.anim({ opacity: 0, time: 1 }, 'linear');
  attacker.anim({ time: 300, opacity: 1 }, 'linear');
  defender.delay(330);
  defender.anim({ z: defender.behind(20), time: 100 }, 'swing');
  defender.anim({ time: 300 }, 'swing');
};

/** PS `BattleOtherAnims.chargestatus` — electro orbs spiral into the user (charge). */
const chargestatus: MoveAnim = (scene: FxScene, [attacker]: Actor[]) => {
  scene.showEffect(
    'electroball',
    { x: attacker.x - 60, y: attacker.y + 40, z: attacker.z, scale: 0.7, opacity: 0.7, time: 0 },
    { x: attacker.x, y: attacker.y, scale: 0.2, opacity: 0.2, time: 300 },
    'linear',
    'fade',
  );
  scene.showEffect(
    'electroball',
    { x: attacker.x + 60, y: attacker.y - 5, z: attacker.z, scale: 0.7, opacity: 0.7, time: 100 },
    { x: attacker.x, y: attacker.y, scale: 0.2, opacity: 0.2, time: 300 },
    'linear',
    'fade',
  );
  scene.showEffect(
    'electroball',
    { x: attacker.x - 30, y: attacker.y + 60, z: attacker.z, scale: 0.7, opacity: 0.7, time: 100 },
    { x: attacker.x, y: attacker.y, scale: 0.2, opacity: 0.2, time: 400 },
    'linear',
    'fade',
  );
  scene.showEffect(
    'electroball',
    { x: attacker.x + 20, y: attacker.y - 50, z: attacker.z, scale: 0.7, opacity: 0.7, time: 100 },
    { x: attacker.x, y: attacker.y, scale: 0.2, opacity: 0.2, time: 400 },
    'linear',
    'fade',
  );
  scene.showEffect(
    'electroball',
    { x: attacker.x - 70, y: attacker.y - 50, z: attacker.z, scale: 0.7, opacity: 0.7, time: 200 },
    { x: attacker.x, y: attacker.y, scale: 0.2, opacity: 0.2, time: 500 },
    'linear',
    'fade',
  );
};

/* ──────────────────────────────────────────────────────────────────────────
 * Move scripts (batch 7).
 * ──────────────────────────────────────────────────────────────────────── */

/**
 * PS `rocktomb` — not present in PS BattleMoveAnims; PS plays the category default.
 * We map it to a mud-rock burst around the defender (mirrors PS `sandtomb`-style
 * mudwisp puffs with a rock toss) so it reads as rock-typed without inventing canon.
 */
const rocktomb: MoveAnim = (scene: FxScene, [, defender]: Actor[]) => {
  scene.showEffect(
    'rock3',
    { x: defender.x + 20, y: defender.y - 30, z: defender.z, scale: 0.2, opacity: 1, time: 0 },
    { x: defender.x + 30, y: defender.y, scale: 0.4, opacity: 0, time: 350 },
    'ballistic',
  );
  scene.showEffect(
    'rock3',
    { x: defender.x - 20, y: defender.y - 30, z: defender.z, scale: 0.2, opacity: 1, time: 200 },
    { x: defender.x - 35, y: defender.y, scale: 0.3, opacity: 0, time: 550 },
    'ballistic',
  );
  scene.showEffect(
    'mudwisp',
    { x: defender.x, y: defender.y - 10, z: defender.z, scale: 0, opacity: 0.5, time: 100 },
    { scale: 3, opacity: 0, time: 600 },
    'decel',
  );
};

/** PS `roleplay` — not present in PS BattleMoveAnims; mapped to PS `dance` (status). */
const roleplay: MoveAnim = dance;

/** PS `rollingkick` — not present in PS BattleMoveAnims; PS plays the physical default. */
const rollingkick: MoveAnim = contactattack;

/** PS `rollout` → PS `fastattack` template. */
const rollout: MoveAnim = fastattack;

/** PS `roost` — feathers settle gently around the resting user/target. */
const roost: MoveAnim = (scene: FxScene, [, defender]: Actor[]) => {
  scene.showEffect(
    'feather',
    { x: defender.x + 50, y: defender.y, z: defender.z, scale: 0.5, opacity: 1, time: 0 },
    { x: defender.x + 5, y: defender.y - 20, scale: 0.1, opacity: 0.4, time: 300 },
    'ballistic2Under',
    'fade',
  );
  scene.showEffect(
    'feather',
    { x: defender.x - 50, y: defender.y, z: defender.z, scale: 0.5, opacity: 1, time: 100 },
    { x: defender.x - 10, y: defender.y - 20, scale: 0.1, opacity: 0.4, time: 400 },
    'ballistic2Under',
    'fade',
  );
  scene.showEffect(
    'feather',
    { x: defender.x + 25, y: defender.y, z: defender.z, scale: 0.5, opacity: 1, time: 200 },
    { y: defender.y - 20, scale: 0.1, opacity: 0.4, time: 500 },
    'ballistic2Under',
    'fade',
  );
  scene.showEffect(
    'feather',
    { x: defender.x - 25, y: defender.y, z: defender.z, scale: 0.5, opacity: 1, time: 300 },
    { y: defender.y - 20, scale: 0.1, opacity: 0.4, time: 600 },
    'ballistic2Under',
    'fade',
  );
};

/** PS `rototiller` — not present in PS BattleMoveAnims; mapped to PS `dance` (status). */
const rototiller: MoveAnim = dance;

/** PS `round` → PS `sound` template. */
const round: MoveAnim = sound;

/** PS `sacredfire` — blue holy flames wash over and burst around the defender. */
const sacredfire: MoveAnim = (scene: FxScene, [attacker, defender]: Actor[]) => {
  scene.backgroundEffect('#2630A9', 900, 0.6);
  scene.showEffect(
    'bluefireball',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 1, opacity: 0.2 },
    { x: defender.x, y: defender.y - 40, z: defender.z, scale: 1.5, opacity: 1, time: 500 },
    'linear',
    'fade',
  );
  scene.showEffect(
    'bluefireball',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 1, opacity: 0.2, time: 50 },
    { x: defender.x, y: defender.y - 40, z: defender.z, scale: 1.5, opacity: 1, time: 550 },
    'linear',
    'fade',
  );
  scene.showEffect(
    'iceball',
    { x: defender.x, y: defender.y, z: defender.z, opacity: 0.8, scale: 0, time: 550 },
    { x: defender.x, y: defender.y, z: defender.z, scale: 4, opacity: 0.3, time: 850 },
    'linear',
    'fade',
  );
  scene.showEffect(
    'iceball',
    { x: defender.x, y: defender.y, z: defender.z, opacity: 0.8, scale: 0, time: 650 },
    { x: defender.x, y: defender.y, z: defender.z, scale: 4, opacity: 0.3, time: 950 },
    'linear',
    'fade',
  );
  scene.showEffect(
    'bluefireball',
    { x: defender.x, y: defender.y - 50, z: defender.z, scale: 0.6, opacity: 0.8, time: 550 },
    { x: defender.x + 60, y: defender.y - 20, z: defender.z, scale: 1.5, opacity: 0.5, time: 825 },
    'decel',
    'explode',
  );
  scene.showEffect(
    'bluefireball',
    { x: defender.x, y: defender.y - 50, z: defender.z, scale: 0.6, opacity: 0.8, time: 575 },
    { x: defender.x - 50, y: defender.y - 20, z: defender.z, scale: 1, opacity: 0.5, time: 850 },
    'decel',
    'explode',
  );
  scene.showEffect(
    'bluefireball',
    { x: defender.x, y: defender.y - 50, z: defender.z, scale: 0.6, opacity: 0.8, time: 600 },
    { x: defender.x - 60, y: defender.y + 20, z: defender.z, scale: 1.5, opacity: 0.5, time: 875 },
    'decel',
    'explode',
  );
  scene.showEffect(
    'bluefireball',
    { x: defender.x, y: defender.y - 50, z: defender.z, scale: 0.6, opacity: 0.8, time: 625 },
    { x: defender.x + 50, y: defender.y + 30, z: defender.z, scale: 1.5, opacity: 0.5, time: 900 },
    'decel',
    'explode',
  );
  scene.showEffect(
    'bluefireball',
    { x: defender.x, y: defender.y - 50, z: defender.z, scale: 0.6, opacity: 0.8, time: 650 },
    { x: defender.x - 10, y: defender.y + 60, z: defender.z, scale: 1.5, opacity: 0.5, time: 925 },
    'decel',
    'explode',
  );
};

/** PS `safeguard` — no animation (PS plays nothing). */
const safeguard: MoveAnim = () => {};

/**
 * PS `sandattack` — not present in PS BattleMoveAnims; PS plays the status default.
 * We map it to a low mud-puff drifting at the target (sand kicked up).
 */
const sandattack: MoveAnim = (scene: FxScene, [attacker, defender]: Actor[]) => {
  scene.showEffect(
    'mudwisp',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 0.1, opacity: 0.6 },
    { x: defender.x, y: defender.y, z: defender.z, scale: 1, opacity: 0.3, time: 400 },
    'decel',
    'explode',
  );
  scene.showEffect(
    'mudwisp',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 0.1, opacity: 0.6, time: 100 },
    { x: defender.x + 20, y: defender.y - 10, z: defender.z, scale: 1, opacity: 0.3, time: 500 },
    'decel',
    'explode',
  );
};

/** PS `sandtomb` — three mud puffs swirl around the trapped defender. */
const sandtomb: MoveAnim = (scene: FxScene, [, defender]: Actor[]) => {
  scene.showEffect(
    'mudwisp',
    { x: defender.x + 40, y: defender.y, z: defender.z, scale: 0, opacity: 0.6 },
    { scale: 3, opacity: 0 },
    'decel',
  );
  scene.showEffect(
    'mudwisp',
    { x: defender.x - 40, y: defender.y - 20, z: defender.z, scale: 0, opacity: 0.6, time: 150 },
    { scale: 3, opacity: 0 },
    'decel',
  );
  scene.showEffect(
    'mudwisp',
    { x: defender.x + 10, y: defender.y + 20, z: defender.z, scale: 0, opacity: 0.6, time: 300 },
    { scale: 3, opacity: 0 },
    'decel',
  );
};

/** PS `sandstorm` → PS `dance` template. */
const sandstorm: MoveAnim = dance;

/** PS `scald` — the hydroshot spray plus rising steam wisps off the defender. */
const scald: MoveAnim = (scene: FxScene, actors: Actor[]) => {
  const [, defender] = actors;
  hydroshot(scene, actors);
  scene.showEffect(
    'wisp',
    { x: defender.x + 30, y: defender.y, z: defender.z, scale: 1, opacity: 1, time: 300 },
    { y: defender.y + 60, opacity: 0.2, time: 700 },
    'linear',
    'fade',
  );
  scene.showEffect(
    'wisp',
    { x: defender.x - 30, y: defender.y, z: defender.z, scale: 1, opacity: 1, time: 400 },
    { y: defender.y + 60, opacity: 0.2, time: 800 },
    'linear',
    'fade',
  );
  scene.showEffect(
    'wisp',
    { x: defender.x + 15, y: defender.y, z: defender.z, scale: 1, opacity: 1, time: 500 },
    { y: defender.y + 60, opacity: 0.2, time: 900 },
    'linear',
    'fade',
  );
  scene.showEffect(
    'wisp',
    { x: defender.x - 15, y: defender.y, z: defender.z, scale: 1, opacity: 1, time: 600 },
    { y: defender.y + 60, opacity: 0.2, time: 1000 },
    'linear',
    'fade',
  );
};

/**
 * PS `scaleshot` — not present in PS BattleMoveAnims; PS plays the physical default.
 * We map it to a quick multi-jab contact lunge.
 */
const scaleshot: MoveAnim = contactattack;

/**
 * PS `scaryface` — not present in PS BattleMoveAnims; PS plays the status default.
 * We map it to a dark intimidating wash + a stare cue at the target.
 */
const scaryface: MoveAnim = (scene: FxScene, [, defender]: Actor[]) => {
  scene.backgroundEffect('#000000', 600, 0.2);
  scene.showEffect(
    'stare',
    { x: defender.x, y: defender.y, z: defender.z, scale: 0.5, opacity: 0, time: 200 },
    { scale: 1.2, opacity: 0.8, time: 500 },
    'decel',
    'fade',
  );
};

/**
 * PS `scorchingsands` — not present in PS BattleMoveAnims; PS plays the special
 * default. We map it to a warm ground wash + fireball lobs (sand on fire).
 */
const scorchingsands: MoveAnim = (scene: FxScene, [attacker, defender]: Actor[]) => {
  scene.backgroundEffect('#c08a3a', 700, 0.2);
  scene.showEffect(
    'fireball',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 0.1, opacity: 0 },
    { x: defender.x, y: defender.y, z: defender.z, scale: 0.7, opacity: 1, time: 400 },
    'ballistic',
    'explode',
  );
  scene.showEffect(
    'mudwisp',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 0.1, opacity: 0, time: 100 },
    { x: defender.x + 30, y: defender.y - 10, z: defender.z, scale: 0.7, opacity: 1, time: 500 },
    'ballistic',
    'explode',
  );
  scene.showEffect(
    'fireball',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 0.1, opacity: 0, time: 200 },
    { x: defender.x - 30, y: defender.y - 5, z: defender.z, scale: 0.7, opacity: 1, time: 600 },
    'ballistic',
    'explode',
  );
};

/** PS `screech` → PS `sound` template. */
const screech: MoveAnim = sound;

/**
 * PS `secretpower` — not present in PS BattleMoveAnims; PS plays the physical
 * default. We map it to a contact lunge with a sparkle on the target.
 */
const secretpower: MoveAnim = (scene: FxScene, actors: Actor[]) => {
  const [, defender] = actors;
  contactattack(scene, actors);
  scene.showEffect(
    'shine',
    { x: defender.x, y: defender.y, z: defender.z, scale: 0.2, opacity: 0.8, time: 450 },
    { scale: 0.6, opacity: 0, time: 700 },
    'decel',
    'fade',
  );
};

/** PS `seedbomb` — three energy seed-bombs lob onto the defender and burst. */
const seedbomb: MoveAnim = (scene: FxScene, [attacker, defender]: Actor[]) => {
  scene.showEffect(
    'energyball',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 0.1, opacity: 0 },
    { x: defender.x, y: defender.y, z: defender.z, scale: 0.7, opacity: 1, time: 400 },
    'ballistic',
    'explode',
  );
  scene.showEffect(
    'energyball',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 0.1, opacity: 0, time: 100 },
    { x: defender.x + 40, y: defender.y - 20, z: defender.z, scale: 0.7, opacity: 1, time: 500 },
    'ballistic',
    'explode',
  );
  scene.showEffect(
    'energyball',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 0.1, opacity: 0, time: 200 },
    { x: defender.x - 30, y: defender.y - 10, z: defender.z, scale: 0.7, opacity: 1, time: 600 },
    'ballistic',
    'explode',
  );
};

/**
 * PS `seismictoss` — the user hauls the target up and slams it down. PS used a
 * remote space-background image; substitute a brief dark backgroundEffect wash.
 */
const seismictoss: MoveAnim = (scene: FxScene, [attacker, defender]: Actor[]) => {
  // PS used a remote bg-space.jpg here; substitute a dark "up in the air" wash.
  scene.backgroundEffect('#000000', 500, 0.6, 300);
  scene.showEffect(
    'wisp',
    { x: defender.x, y: defender.y + 10, z: defender.z, scale: 0, opacity: 0.5, time: 400 },
    { scale: 3, opacity: 0, time: 600 },
    'linear',
  );
  attacker.anim(
    { x: defender.x, y: defender.y, z: defender.behind(-5), time: 350 },
    'ballistic2Under',
  );
  attacker.anim(
    { x: defender.x, y: defender.y + 10, z: defender.behind(5), time: 50 },
    'ballistic2Under',
  );
  attacker.anim({ time: 500 }, 'ballistic2Back');
  defender.delay(380);
  defender.anim({ y: defender.y + 100, z: defender.behind(5), opacity: 0.5, time: 300 }, 'decel');
  defender.anim({ time: 250 }, 'accel');
  defender.anim({ x: defender.x, y: defender.y - 35, yscale: 0.25, time: 50 }, 'swing');
  defender.anim({ time: 300 }, 'swing');
  scene.wait(1000);
};

/**
 * PS `selfdestruct` — not present in PS BattleMoveAnims; PS plays the physical
 * default. We map it to a bright burst at the user that flares over the field.
 */
const selfdestruct: MoveAnim = (scene: FxScene, [attacker, defender]: Actor[]) => {
  scene.backgroundEffect('#ffffff', 600, 0.5, 100);
  scene.showEffect(
    'fireball',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 1, opacity: 0.8, time: 100 },
    { scale: 6, opacity: 0, time: 500 },
    'linear',
    'fade',
  );
  scene.showEffect(
    'fireball',
    { x: defender.x, y: defender.y, z: defender.z, scale: 1, opacity: 0.8, time: 250 },
    { scale: 5, opacity: 0, time: 650 },
    'linear',
    'fade',
  );
  defender.delay(250);
  defender.anim({ z: defender.behind(20), time: 100 }, 'swing');
  defender.anim({ time: 300 }, 'swing');
};

/** PS `shadowclaw` — a dark wash, then the claw-attack double swipe. */
const shadowclaw: MoveAnim = (scene: FxScene, actors: Actor[]) => {
  scene.backgroundEffect('#000000', 700, 0.3);
  clawattack(scene, actors);
};

/** PS `shadowpunch` — a dark wash, the user phases in for a ghostly fist jab. */
const shadowpunch: MoveAnim = (scene: FxScene, [attacker, defender]: Actor[]) => {
  scene.backgroundEffect('#000000', 700, 0.3);
  scene.showEffect(
    'wisp',
    { x: defender.x, y: defender.y, z: defender.z, scale: 0, opacity: 1, time: 400 },
    {
      x: defender.leftof(-20),
      y: defender.y,
      z: defender.behind(20),
      scale: 3,
      opacity: 0,
      time: 700,
    },
    'linear',
  );
  scene.showEffect(
    'wisp',
    { x: defender.x, y: defender.y, z: defender.z, scale: 0, opacity: 1, time: 500 },
    {
      x: defender.leftof(-20),
      y: defender.y,
      z: defender.behind(20),
      scale: 3,
      opacity: 0,
      time: 800,
    },
    'linear',
  );
  scene.showEffect(
    'fist',
    { x: defender.x, y: defender.y, z: defender.z, scale: 1, opacity: 1, time: 400 },
    {
      x: defender.leftof(-20),
      y: defender.y,
      z: defender.behind(20),
      scale: 2,
      opacity: 0,
      time: 800,
    },
    'linear',
  );
  attacker.anim(
    { x: attacker.leftof(-20), y: attacker.y, z: attacker.behind(-20), opacity: 0, time: 200 },
    'linear',
  );
  attacker.anim(
    { x: defender.x, y: defender.y, z: defender.behind(-120), opacity: 0, time: 1 },
    'linear',
  );
  attacker.anim(
    { x: defender.x, y: defender.y, z: defender.behind(5), opacity: 1, time: 200 },
    'linear',
  );
  attacker.anim(
    { x: defender.x, y: defender.y, z: defender.behind(-25), opacity: 0, time: 300 },
    'linear',
  );
  attacker.anim({ opacity: 0, time: 1 }, 'linear');
  attacker.anim({ time: 300, opacity: 1 }, 'linear');
  defender.delay(400);
  defender.anim({ z: defender.behind(20), time: 100 }, 'swing');
  defender.anim({ time: 300 }, 'swing');
};

/** PS `shadowsneak` — a dark wash, then the sneak-attack blink-through. */
const shadowsneak: MoveAnim = (scene: FxScene, actors: Actor[]) => {
  scene.backgroundEffect('#000000', 700, 0.3);
  sneakattack(scene, actors);
};

/** PS `sharpen` → PS `selfstatus` template. */
const sharpen: MoveAnim = selfstatus;

/**
 * PS `shedtail` — not present in PS BattleMoveAnims; PS plays the status default.
 * We map it to a self-status pulse + a small wisp dropping behind the user (decoy).
 */
const shedtail: MoveAnim = (scene: FxScene, [attacker]: Actor[]) => {
  selfstatus(scene, [attacker]);
  scene.showEffect(
    'wisp',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 1, opacity: 0.6, time: 400 },
    {
      x: attacker.leftof(40),
      y: attacker.y,
      z: attacker.behind(20),
      scale: 0.6,
      opacity: 0,
      time: 800,
    },
    'decel',
    'fade',
  );
};

/**
 * PS `sheercold` — a huge icicle slams down on the target. PS used a remote
 * ice-cave background image; substitute a pale-blue backgroundEffect wash.
 */
const sheercold: MoveAnim = (scene: FxScene, [, defender]: Actor[]) => {
  // PS used a remote bg-icecave.jpg here; substitute a frosty pale-blue wash.
  scene.backgroundEffect('#9fd6e6', 1000, 0.6);
  scene.showEffect(
    'icicle',
    { x: defender.x, y: defender.y, z: defender.z, xscale: 2, yscale: 5, opacity: 0.6 },
    { xscale: 2.2, yscale: 5.25, scale: 0.6, time: 800 },
    'linear',
    'explode',
  );
};

/** PS `shellsmash` — the user cracks its shell off in an explosive flurry. */
const shellsmash: MoveAnim = (scene: FxScene, [attacker]: Actor[]) => {
  scene.showEffect(
    'shell',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 1, opacity: 0.5, time: 0 },
    { scale: 0.8, opacity: 0.8, time: 400 },
    'linear',
    'explode',
  );
  scene.showEffect(
    'impact',
    { x: attacker.x + 40, y: attacker.y, z: attacker.z, scale: 0.7, opacity: 0.4, time: 400 },
    { scale: 1.2, opacity: 0, time: 600 },
    'linear',
  );
  scene.showEffect(
    'impact',
    { x: attacker.x - 40, y: attacker.y - 20, z: attacker.z, scale: 0.7, opacity: 0.4, time: 450 },
    { scale: 1.2, opacity: 0, time: 650 },
    'linear',
  );
  scene.showEffect(
    'impact',
    { x: attacker.x + 10, y: attacker.y + 20, z: attacker.z, scale: 0.7, opacity: 0.4, time: 560 },
    { scale: 1.2, opacity: 0, time: 700 },
    'linear',
  );
  attacker.anim({ scale: 0.4, opacity: 0, time: 400 }, 'linear');
  attacker.delay(75);
  attacker.anim({ x: attacker.x - 20, time: 75 });
  attacker.anim({ x: attacker.x + 20, time: 100 });
  attacker.anim({ x: attacker.x - 20, time: 100 });
  attacker.anim({ x: attacker.x + 20, time: 100 });
  attacker.anim({ x: attacker.x - 20, time: 100 });
  attacker.anim({ x: attacker.x + 20, time: 100 });
  attacker.anim({ x: attacker.x, time: 75 });
};

/** PS `shockwave` — two horizontal electro arcs sweep off the user into the target. */
const shockwave: MoveAnim = (scene: FxScene, [attacker, defender]: Actor[]) => {
  scene.showEffect(
    'electroball',
    {
      x: attacker.x,
      y: attacker.y - 70,
      z: attacker.z,
      scale: 1,
      xscale: 5,
      opacity: 0.6,
      time: 0,
    },
    { scale: 3, xscale: 10, opacity: 0.1, time: 600 },
    'decel',
    'fade',
  );
  scene.showEffect(
    'electroball',
    {
      x: attacker.x,
      y: attacker.y - 70,
      z: attacker.z,
      scale: 1,
      xscale: 5,
      opacity: 0.6,
      time: 300,
    },
    { scale: 3, xscale: 10, opacity: 0.1, time: 700 },
    'decel',
    'fade',
  );
  scene.showEffect(
    'electroball',
    { x: defender.x, y: defender.y, z: defender.z, opacity: 0.3, scale: 0, time: 250 },
    { x: defender.x, y: defender.y, z: defender.z, scale: 2, opacity: 0, time: 550 },
    'linear',
  );
  scene.showEffect(
    'electroball',
    { x: defender.x, y: defender.y, z: defender.z, opacity: 0.3, scale: 0, time: 500 },
    { x: defender.x, y: defender.y, z: defender.z, scale: 2, opacity: 0, time: 800 },
    'linear',
  );
};

/** PS `signalbeam` — a four-orb energy/electro beam streams onto the defender. */
const signalbeam: MoveAnim = (scene: FxScene, [attacker, defender]: Actor[]) => {
  scene.showEffect(
    'energyball',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 0.5, opacity: 0.2 },
    { x: defender.x, y: defender.y, z: defender.behind(20), opacity: 0.6, time: 400 },
    'linear',
    'explode',
  );
  scene.showEffect(
    'electroball',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 0.5, opacity: 0.2, time: 100 },
    { x: defender.x + 10, y: defender.y - 5, z: defender.behind(20), opacity: 0.6, time: 500 },
    'linear',
    'explode',
  );
  scene.showEffect(
    'energyball',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 0.5, opacity: 0.2, time: 200 },
    { x: defender.x - 10, y: defender.y + 5, z: defender.behind(20), opacity: 0.6, time: 600 },
    'linear',
    'explode',
  );
  scene.showEffect(
    'energyball',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 0.5, opacity: 0.2, time: 300 },
    { x: defender.x, y: defender.y - 5, z: defender.behind(20), opacity: 0.6, time: 700 },
    'linear',
    'explode',
  );
};

/**
 * PS `silverwind` — not present in PS BattleMoveAnims; PS plays the special default.
 * We map it to silvery wisps + feathers streaming onto the defender.
 */
const silverwind: MoveAnim = (scene: FxScene, [attacker, defender]: Actor[]) => {
  scene.showEffect(
    'wisp',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 0.3, opacity: 0.5 },
    { x: defender.x, y: defender.y, z: defender.behind(20), scale: 1, opacity: 0.3, time: 400 },
    'decel',
    'explode',
  );
  scene.showEffect(
    'feather',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 0.3, opacity: 0.8, time: 100 },
    {
      x: defender.x + 20,
      y: defender.y - 10,
      z: defender.behind(20),
      scale: 0.6,
      opacity: 0,
      time: 600,
    },
    'decel',
    'fade',
  );
  scene.showEffect(
    'feather',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 0.3, opacity: 0.8, time: 200 },
    {
      x: defender.x - 20,
      y: defender.y + 10,
      z: defender.behind(20),
      scale: 0.6,
      opacity: 0,
      time: 700,
    },
    'decel',
    'fade',
  );
};

/** PS `simplebeam` — a four-orb iceball beam streams onto the defender. */
const simplebeam: MoveAnim = (scene: FxScene, [attacker, defender]: Actor[]) => {
  scene.showEffect(
    'iceball',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 0.5, opacity: 0.2 },
    { x: defender.x, y: defender.y, z: defender.behind(20), opacity: 0.6, time: 400 },
    'linear',
    'explode',
  );
  scene.showEffect(
    'iceball',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 0.5, opacity: 0.2, time: 100 },
    { x: defender.x + 10, y: defender.y - 5, z: defender.behind(20), opacity: 0.6, time: 500 },
    'linear',
    'explode',
  );
  scene.showEffect(
    'iceball',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 0.5, opacity: 0.2, time: 200 },
    { x: defender.x - 10, y: defender.y + 5, z: defender.behind(20), opacity: 0.6, time: 600 },
    'linear',
    'explode',
  );
  scene.showEffect(
    'iceball',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 0.5, opacity: 0.2, time: 300 },
    { x: defender.x, y: defender.y - 5, z: defender.behind(20), opacity: 0.6, time: 700 },
    'linear',
    'explode',
  );
};

/** PS `sing` → PS `sound` template. */
const sing: MoveAnim = sound;

/** PS `sketch` → PS `dance` template. */
const sketch: MoveAnim = dance;

/** PS `skillswap` — two pairs of wisps cross between user and target. */
const skillswap: MoveAnim = (scene: FxScene, [attacker, defender]: Actor[]) => {
  scene.showEffect(
    'wisp',
    { x: attacker.x, y: attacker.y - 30, z: attacker.z, scale: 1, opacity: 1, time: 0 },
    { x: defender.x, y: defender.y, z: defender.z, scale: 0.5, opacity: 0, time: 400 },
    'ballistic2Under',
  );
  scene.showEffect(
    'wisp',
    { x: attacker.x, y: attacker.y - 30, z: attacker.z, scale: 1, opacity: 1, time: 200 },
    { x: defender.x, y: defender.y, z: defender.z, scale: 0.5, opacity: 0, time: 600 },
    'ballistic2Under',
  );
  scene.showEffect(
    'wisp',
    { x: defender.x, y: defender.y, z: defender.z, scale: 1, opacity: 1, time: 200 },
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 0.5, opacity: 0, time: 600 },
    'ballistic',
  );
  scene.showEffect(
    'wisp',
    { x: defender.x, y: defender.y, z: defender.z, scale: 1, opacity: 1, time: 400 },
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 0.5, opacity: 0, time: 800 },
    'ballistic',
  );
};

/**
 * PS `skittersmack` — not present in PS BattleMoveAnims; PS plays the physical
 * default. We map it to a contact lunge with a bug-green wisp smear on the target.
 */
const skittersmack: MoveAnim = (scene: FxScene, actors: Actor[]) => {
  const [, defender] = actors;
  contactattack(scene, actors);
  scene.showEffect(
    'energyball',
    { x: defender.x, y: defender.y, z: defender.z, scale: 0, opacity: 0.6, time: 450 },
    { scale: 2, opacity: 0, time: 750 },
    'linear',
    'fade',
  );
};

/** PS `skullbash` → PS `contactattack`, with a charging prepareAnim. */
const skullbash: MoveAnimEntry = {
  anim: contactattack,
  prepareAnim(_scene: FxScene, [attacker]: Actor[]) {
    attacker.anim({ opacity: 0.8, time: 300 }, 'linear');
  },
};

/** PS `skyattack` → PS `flight` template, with a charging prepareAnim. */
const skyattack: MoveAnimEntry = {
  anim(scene: FxScene, [attacker, defender]: Actor[]) {
    // PS `BattleOtherAnims.flight`: fly off-screen, swoop back through the target.
    attacker.anim(
      { x: attacker.leftof(-200), y: attacker.y + 80, z: attacker.z, opacity: 0, time: 350 },
      'accel',
    );
    attacker.anim(
      { x: defender.leftof(-200), y: defender.y + 80, z: defender.z, time: 1 },
      'linear',
    );
    attacker.anim({ x: defender.x, y: defender.y, z: defender.z, opacity: 1, time: 350 }, 'accel');
    scene.showEffect(
      'wisp',
      { x: defender.x, y: defender.y, z: defender.z, scale: 0, opacity: 0.5, time: 700 },
      { scale: 2, opacity: 0, time: 900 },
      'linear',
    );
    attacker.anim({
      x: defender.leftof(100),
      y: defender.y - 40,
      z: defender.z,
      opacity: 0,
      time: 175,
    });
    attacker.anim({ x: attacker.x, y: attacker.y + 40, z: attacker.behind(40), time: 1 });
    attacker.anim({ x: attacker.x, y: attacker.y, z: attacker.z, time: 250 }, 'decel');
    defender.delay(700);
    defender.anim({ z: defender.behind(20), time: 100 }, 'swing');
    defender.anim({ time: 300 }, 'swing');
  },
  prepareAnim(_scene: FxScene, [attacker]: Actor[]) {
    attacker.anim({ opacity: 0.8, time: 300 }, 'linear');
  },
};

/** PS `skydrop` → PS `contactattack`, with a charging prepareAnim that lifts both. */
const skydrop: MoveAnimEntry = {
  anim: contactattack,
  prepareAnim(_scene: FxScene, [attacker, defender]: Actor[]) {
    attacker.anim({ opacity: 0.2, y: attacker.y + 80, time: 300 }, 'linear');
    defender.anim({ opacity: 0.2, y: defender.y + 80, time: 300 }, 'linear');
  },
};

/** PS `skyuppercut` — a rising fist launches the target skyward. */
const skyuppercut: MoveAnim = (scene: FxScene, [attacker, defender]: Actor[]) => {
  scene.showEffect(
    'wisp',
    { x: defender.x, y: defender.y + 10, z: defender.z, scale: 0, opacity: 0.5, time: 400 },
    { scale: 3, opacity: 0, time: 600 },
    'linear',
  );
  scene.showEffect(
    'fist1',
    {
      x: defender.leftof(-20),
      y: defender.y + 10,
      z: defender.behind(20),
      scale: 1.2,
      opacity: 0.8,
      time: 400,
    },
    { y: defender.y + 80, opacity: 1, time: 500 },
    'decel',
    'explode',
  );
  attacker.anim(
    { x: defender.x, y: defender.y, z: defender.behind(-5), time: 350 },
    'ballistic2Under',
  );
  attacker.anim({ x: defender.x, y: defender.y + 80, z: defender.behind(5), time: 300 }, 'decel');
  attacker.anim({ x: defender.x, y: defender.y, z: defender.behind(-5), time: 250 }, 'accel');
  attacker.anim({ time: 500 }, 'ballistic2Back');
  defender.delay(380);
  defender.anim({ y: defender.y + 100, z: defender.behind(5), opacity: 0.5, time: 300 }, 'decel');
  defender.anim({ time: 250 }, 'accel');
  defender.anim({ x: defender.x, y: defender.y - 30, yscale: 0.25, time: 50 }, 'swing');
  defender.anim({ time: 300 }, 'swing');
};

/**
 * PS `slackoff` — not present in PS BattleMoveAnims; PS plays the status default.
 * We map it to PS `selfstatus` (a calm self-pulse) for the recovery.
 */
const slackoff: MoveAnim = selfstatus;

/** PS `slam` → PS `contactattack` template. */
const slam: MoveAnim = contactattack;

/** PS `slash` → PS `slashattack` template. */
const slash: MoveAnim = slashattack;

/**
 * PS `sleeppowder` — not present in PS BattleMoveAnims; PS plays the status default.
 * We map it to drifting green energy spores settling on the target.
 */
const sleeppowder: MoveAnim = (scene: FxScene, [attacker, defender]: Actor[]) => {
  scene.showEffect(
    'energyball',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 0.1, opacity: 0.5 },
    { x: defender.x + 20, y: defender.y + 10, z: defender.z, scale: 0.6, opacity: 0.3, time: 500 },
    'ballistic',
    'fade',
  );
  scene.showEffect(
    'energyball',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 0.1, opacity: 0.5, time: 100 },
    { x: defender.x - 20, y: defender.y, z: defender.z, scale: 0.6, opacity: 0.3, time: 600 },
    'ballistic',
    'fade',
  );
  scene.showEffect(
    'energyball',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 0.1, opacity: 0.5, time: 200 },
    { x: defender.x, y: defender.y - 10, z: defender.z, scale: 0.6, opacity: 0.3, time: 700 },
    'ballistic',
    'fade',
  );
};

/** PS `sleeptalk` — no animation (PS plays nothing). */
const sleeptalk: MoveAnim = () => {};

/** PS `sludge` — three poison globs lob onto the defender and fade. */
const sludge: MoveAnim = (scene: FxScene, [attacker, defender]: Actor[]) => {
  scene.showEffect(
    'poisonwisp',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 0.1, opacity: 0 },
    { x: defender.x, y: defender.y, z: defender.z, scale: 0.7, opacity: 1, time: 400 },
    'ballistic',
    'fade',
  );
  scene.showEffect(
    'poisonwisp',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 0.1, opacity: 0, time: 100 },
    { x: defender.x, y: defender.y, z: defender.z, scale: 0.7, opacity: 1, time: 500 },
    'ballistic',
    'fade',
  );
  scene.showEffect(
    'poisonwisp',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 0.1, opacity: 0, time: 200 },
    { x: defender.x, y: defender.y, z: defender.z, scale: 0.7, opacity: 1, time: 600 },
    'ballistic',
    'fade',
  );
};

/**
 * PS `sludgewave` — a purple wash, the defender rocks, then poison wisps burst.
 * PS iterated over multiple defenders; in our 1v1 engine we play the single target.
 */
const sludgewave: MoveAnim = (scene: FxScene, [attacker, defender]: Actor[]) => {
  defender.delay(125);
  defender.anim({ z: defender.behind(5), time: 75 }, 'swing');
  defender.anim({ time: 75 }, 'swing');
  defender.anim({ z: defender.behind(5), time: 75 }, 'swing');
  defender.anim({ time: 75 }, 'swing');
  defender.anim({ z: defender.behind(5), time: 75 }, 'swing');
  defender.anim({ time: 75 }, 'swing');

  scene.backgroundEffect('#AA00AA', 700, 0.2);
  scene.showEffect(
    'poisonwisp',
    { x: attacker.x, y: attacker.y - 25, z: attacker.z, scale: 0.3, opacity: 0.3 },
    { x: defender.x, y: defender.y + 10, z: defender.behind(50), scale: 2, opacity: 0.6 },
    'decel',
    'explode',
  );
  scene.showEffect(
    'poisonwisp',
    { x: attacker.x - 30, y: attacker.y - 25, z: attacker.z, scale: 0.3, opacity: 0.3 },
    { x: defender.x - 60, y: defender.y, z: defender.behind(50), scale: 2, opacity: 0.6 },
    'decel',
    'explode',
  );
  scene.showEffect(
    'poisonwisp',
    { x: attacker.x + 30, y: attacker.y - 25, z: attacker.z, scale: 0.3, opacity: 0.3 },
    { x: defender.x + 60, y: defender.y, z: defender.behind(50), scale: 2, opacity: 0.6 },
    'decel',
    'explode',
  );
};

/**
 * PS `smackdown` — not present in PS BattleMoveAnims; PS plays the physical default.
 * We map it to a rock hurled down onto the target + a contact bump.
 */
const smackdown: MoveAnim = (scene: FxScene, actors: Actor[]) => {
  const [, defender] = actors;
  scene.showEffect(
    'rock1',
    { x: defender.x, y: defender.y + 100, z: defender.z, opacity: 0, scale: 0.5 },
    { y: defender.y - 30, opacity: 1, time: 300 },
    'accel',
    'explode',
  );
  fastattack(scene, actors);
};

/** PS `smartstrike` — two slashes + an iceball flash, then the user darts through. */
const smartstrike: MoveAnim = (scene: FxScene, [attacker, defender]: Actor[]) => {
  scene.showEffect(
    'rightslash',
    { x: defender.x, y: defender.y, z: defender.z, scale: 1, opacity: 0.5 },
    { opacity: 1, time: 400 },
    'linear',
    'explode',
  );
  scene.showEffect(
    'leftslash',
    { x: defender.x, y: defender.y, z: defender.z, scale: 1, opacity: 0.5 },
    { opacity: 1, time: 400 },
    'linear',
    'explode',
  );
  scene.showEffect(
    'iceball',
    { x: defender.x, y: defender.y, z: defender.z, scale: 1, opacity: 1 },
    { opacity: 0.5, time: 400 },
    'linear',
    'explode',
  );
  scene.showEffect(
    'wisp',
    { x: defender.x, y: defender.y, z: defender.z, scale: 0, opacity: 0.5, time: 860 },
    { scale: 2, opacity: 0, time: 1160 },
    'linear',
  );
  scene.showEffect(
    'wisp',
    { x: defender.x, y: defender.y, z: defender.z, scale: 0, opacity: 0.5, time: 910 },
    { scale: 2, opacity: 0, time: 1210 },
    'linear',
  );
  // PS drew two attacker-sprite streaks here (showEffect(attacker.sp, …)); omitted
  // (no sprite-as-effect in our engine) — the attacker's dart-through lunge below
  // preserves the read.
  attacker.delay(500);
  attacker.anim(
    { x: defender.x, y: defender.y, z: defender.behind(70), time: 300, opacity: 0.5 },
    'accel',
  );
  attacker.anim(
    { x: defender.x, y: defender.x, z: defender.behind(100), opacity: 0, time: 100 },
    'linear',
  );
  attacker.anim(
    { x: attacker.x, y: attacker.y, z: attacker.behind(70), opacity: 0, time: 1 },
    'linear',
  );
  attacker.anim({ opacity: 1, time: 500 }, 'decel');
  defender.delay(760);
  defender.anim({ z: defender.behind(30), time: 100 }, 'swing');
  defender.anim({ time: 300 }, 'swing');
};

/** PS `smellingsalts` — wisps + a hard chop jolt the target awake. */
const smellingsalts: MoveAnim = (scene: FxScene, [attacker, defender]: Actor[]) => {
  scene.showEffect(
    'wisp',
    { x: defender.x, y: defender.y, z: defender.z, scale: 0, opacity: 1, time: 400 },
    {
      x: defender.leftof(-20),
      y: defender.y,
      z: defender.behind(20),
      scale: 3,
      opacity: 0,
      time: 700,
    },
    'linear',
  );
  scene.showEffect(
    'wisp',
    { x: defender.x, y: defender.y, z: defender.z, scale: 0, opacity: 1, time: 500 },
    {
      x: defender.leftof(-20),
      y: defender.y,
      z: defender.behind(20),
      scale: 3,
      opacity: 0,
      time: 800,
    },
    'linear',
  );
  scene.showEffect(
    'rightchop',
    { x: defender.x, y: defender.y, z: defender.z, scale: 0.6, opacity: 1, time: 400 },
    {
      x: defender.leftof(-20),
      y: defender.y,
      z: defender.behind(20),
      scale: 0.9,
      opacity: 0,
      time: 800,
    },
    'linear',
  );
  attacker.anim(
    { x: defender.leftof(20), y: defender.y, z: defender.behind(-20), time: 400 },
    'ballistic2Under',
  );
  attacker.anim({ x: defender.x, y: defender.y, z: defender.z, time: 50 });
  attacker.anim({ time: 500 }, 'ballistic2');
  defender.delay(425);
  defender.anim(
    { x: defender.leftof(-15), y: defender.y, z: defender.behind(15), time: 50 },
    'swing',
  );
  defender.anim({ time: 300 }, 'swing');
};

/** PS `smog` — a poison cloud rolls onto the defender and dissipates. */
const smog: MoveAnim = (scene: FxScene, [attacker, defender]: Actor[]) => {
  scene.showEffect(
    'poisonwisp',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 0.7, opacity: 0.6, time: 0 },
    {
      x: defender.x,
      y: defender.y + 10,
      z: defender.behind(10),
      scale: 1,
      opacity: 0.3,
      time: 400,
    },
    'decel',
    'explode',
  );
  scene.showEffect(
    'poisonwisp',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 0.7, opacity: 1, time: 100 },
    {
      x: defender.x - 20,
      y: defender.y + 5,
      z: defender.behind(10),
      scale: 1,
      opacity: 0.3,
      time: 500,
    },
    'decel',
    'explode',
  );
  scene.showEffect(
    'poisonwisp',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 0.7, opacity: 1, time: 200 },
    {
      x: defender.x + 25,
      y: defender.y,
      z: defender.behind(10),
      scale: 1,
      opacity: 0.3,
      time: 600,
    },
    'decel',
    'explode',
  );
  scene.showEffect(
    'poisonwisp',
    { x: defender.x + 30, y: defender.y, z: defender.z, scale: 1, opacity: 1, time: 400 },
    { x: defender.x + 50, y: defender.y + 30, scale: 1.4, opacity: 0.2, time: 800 },
    'decel',
    'fade',
  );
  scene.showEffect(
    'poisonwisp',
    { x: defender.x - 30, y: defender.y, z: defender.z, scale: 1, opacity: 1, time: 500 },
    { x: defender.x - 50, y: defender.y + 30, scale: 1.4, opacity: 0.2, time: 900 },
    'decel',
    'fade',
  );
  scene.showEffect(
    'poisonwisp',
    { x: defender.x + 15, y: defender.y, z: defender.z, scale: 1, opacity: 1, time: 600 },
    { x: defender.x + 25, y: defender.y + 20, scale: 1.4, opacity: 0.2, time: 1000 },
    'decel',
    'fade',
  );
};

/** PS `smokescreen` — three black smoke globs lob onto the defender and burst. */
const smokescreen: MoveAnim = (scene: FxScene, [attacker, defender]: Actor[]) => {
  scene.showEffect(
    'blackwisp',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 0.1, opacity: 0 },
    { x: defender.x, y: defender.y, z: defender.z, scale: 0.7, opacity: 1, time: 400 },
    'ballistic',
    'explode',
  );
  scene.showEffect(
    'blackwisp',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 0.1, opacity: 0, time: 100 },
    { x: defender.x + 40, y: defender.y - 20, z: defender.z, scale: 0.7, opacity: 1, time: 500 },
    'ballistic',
    'explode',
  );
  scene.showEffect(
    'blackwisp',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 0.1, opacity: 0, time: 200 },
    { x: defender.x - 30, y: defender.y - 10, z: defender.z, scale: 0.7, opacity: 1, time: 600 },
    'ballistic',
    'explode',
  );
};

/** PS `snarl` — three dark shadow rings burst outward from the user. */
const snarl: MoveAnim = (scene: FxScene, [attacker]: Actor[]) => {
  scene.showEffect(
    'shadowball',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 0, opacity: 0.4, time: 0 },
    { scale: 7, opacity: 0, time: 400 },
    'linear',
  );
  scene.showEffect(
    'shadowball',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 0, opacity: 0.4, time: 150 },
    { scale: 7, opacity: 0, time: 600 },
    'linear',
  );
  scene.showEffect(
    'shadowball',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 0, opacity: 0.4, time: 300 },
    { scale: 7, opacity: 0, time: 800 },
    'linear',
  );
};

/** PS `snatch` → PS `dance` template. */
const snatch: MoveAnim = dance;

/** PS `snore` → PS `sound` template. */
const snore: MoveAnim = sound;

/** PS `snowscape` → PS `dance` template. */
const snowscape: MoveAnim = dance;

/** PS `soak` → PS `hydroshot` template. */
const soak: MoveAnim = hydroshot;

/** PS `softboiled` — a soft warm orb + wisp swell at the user (recovery). */
const softboiled: MoveAnim = (scene: FxScene, [attacker]: Actor[]) => {
  scene.showEffect(
    'iceball',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 0.75, yscale: 1, opacity: 0.2, time: 0 },
    { scale: 0.5, yscale: 0.75, opacity: 0.5, time: 400 },
    'linear',
    'explode',
  );
  scene.showEffect(
    'wisp',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 1, yscale: 1.5, opacity: 0.2, time: 0 },
    { scale: 0.6, yscale: 0.75, opacity: 0.8, time: 400 },
    'linear',
    'explode',
  );
};

/**
 * PS `solarblade` — a charged sword slash launches energy/fire orbs onto the target.
 * PS used a remote sunny-day background image; substitute a warm yellow wash.
 * Charging move: prepareAnim = PS `chargestatus`.
 */
const solarblade: MoveAnimEntry = {
  anim(scene: FxScene, [attacker, defender]: Actor[]) {
    const xstep = 0;
    const ystep = 20;
    const zstep = 0;

    // PS used a remote weather-sunnyday.jpg here; substitute a warm sunlight wash.
    scene.backgroundEffect('#f6e08a', 900, 0.5);

    scene.showEffect(
      'sword',
      { x: attacker.leftof(10), y: attacker.y - 10, z: attacker.z, scale: 0.5, opacity: 1 },
      { y: attacker.y + 10, scale: 1, opacity: 0.4, time: 300 },
      'decel',
      'fade',
    );

    for (let i = 0; i < 5; i++) {
      scene.showEffect(
        'wisp',
        {
          x: attacker.leftof(10) + xstep * (i + 1),
          y: attacker.y - 20 + ystep * (i + 1),
          z: attacker.z + zstep * (i + 1),
          scale: 1.5,
          opacity: 1,
          time: 40 * i + 0,
        },
        { opacity: 0, time: 45 * i + 500 },
        'linear',
      );
    }

    scene.showEffect(
      'flareball',
      { x: defender.x + 30, y: defender.y + 25, z: defender.z, scale: 0.2, opacity: 1, time: 500 },
      { x: defender.x + 50, y: defender.y + 10, opacity: 0.5, time: 800 },
      'accel',
      'explode',
    );
    scene.showEffect(
      'energyball',
      { x: defender.x - 30, y: defender.y - 20, z: defender.z, scale: 0.2, opacity: 1, time: 600 },
      { x: defender.x - 50, y: defender.y - 20, opacity: 0.5, time: 800 },
      'accel',
      'explode',
    );
    scene.showEffect(
      'energyball',
      { x: defender.x + 15, y: defender.y + 10, z: defender.z, scale: 0.2, opacity: 1, time: 700 },
      { x: defender.x + 35, y: defender.y + 30, opacity: 0.5, time: 900 },
      'accel',
      'explode',
    );
    scene.showEffect(
      'flareball',
      { x: defender.x - 15, y: defender.y - 30, z: defender.z, scale: 0.2, opacity: 1, time: 700 },
      { x: defender.x - 35, y: defender.y - 40, opacity: 0.5, time: 900 },
      'accel',
      'explode',
    );
    scene.showEffect(
      'flareball',
      {
        x: defender.x + 10,
        y: defender.y - 10,
        z: defender.z,
        scale: 1.5,
        opacity: 0.6,
        time: 700,
      },
      { scale: 2, opacity: 0, time: 1000 },
      'accel',
      'fade',
    );
    scene.showEffect(
      'leftslash',
      { x: defender.x - 10, y: defender.y - 10, z: defender.z, scale: 3, opacity: 0.6, time: 600 },
      { scale: 3.5, opacity: 0, time: 1000 },
      'accel',
      'fade',
    );

    defender.delay(550);
    defender.anim({ z: defender.behind(20), time: 200 }, 'swing');
    defender.anim({ time: 300 }, 'swing');
  },
  prepareAnim: chargestatus,
};

/** PS `sonicboom` → PS `sound` template. */
const sonicboom: MoveAnim = sound;

/* ──────────────────────────────────────────────────────────────────────────
 * The batch-7 table, keyed by Showdown move id (toID form).
 * ──────────────────────────────────────────────────────────────────────── */

export const SHOWDOWN_BATCH_7: Record<string, MoveAnim | MoveAnimEntry> = {
  rocktomb,
  roleplay,
  rollingkick,
  rollout,
  roost,
  rototiller,
  round,
  sacredfire,
  safeguard,
  sandattack,
  sandtomb,
  sandstorm,
  scald,
  scaleshot,
  scaryface,
  scorchingsands,
  screech,
  secretpower,
  seedbomb,
  seismictoss,
  selfdestruct,
  shadowclaw,
  shadowpunch,
  shadowsneak,
  sharpen,
  shedtail,
  sheercold,
  shellsmash,
  shockwave,
  signalbeam,
  silverwind,
  simplebeam,
  sing,
  sketch,
  skillswap,
  skittersmack,
  skullbash,
  skyattack,
  skydrop,
  skyuppercut,
  slackoff,
  slam,
  slash,
  sleeppowder,
  sleeptalk,
  sludge,
  sludgewave,
  smackdown,
  smartstrike,
  smellingsalts,
  smog,
  smokescreen,
  snarl,
  snatch,
  snore,
  snowscape,
  soak,
  softboiled,
  solarblade,
  sonicboom,
};
